import React, { useEffect, useRef, useState } from "react";

import WappIcono from "../../../components/wappIcono";
import { GRUPOS } from "./equipoEmojis";
import "./iconoCanalPicker.css";

/** Los emojis que se ofrecen para canales y grupos de WappTeam (áreas de la escuela, cursos, países). */
export const ICONOS_CANAL = [
	"💰", "🧾", "💳", "📊", "📚", "🎓", "🏫", "🧑‍🏫",
	"📝", "📅", "📣", "💬", "📞", "🤝", "📦", "🛒",
	"💇‍♀️", "💈", "✂️", "💅", "💄", "🍰", "🧁", "🍳",
	"☕", "🔧", "❄️", "🔌", "📸", "🎨", "🖥️", "💡",
	"⭐", "🔥", "🏆", "🎯", "🚀", "🎉", "🇲🇽", "🇭🇳",
];

// Más emojis para el ícono (Esteban, 2026-10-12: "un set mucho más grande"): los del selector del compositor más animales y cosas.
const EXTRA: { n: string; i: string; e: string }[] = [
	{ n: "Animales", i: "🦁", e: "🦁 🐯 🐆 🐅 🐺 🦊 🐻 🐼 🐨 🐮 🐷 🐸 🐵 🦄 🐴 🦅 🦉 🐦 🐧 🦜 🦩 🕊️ 🐝 🦋 🐞 🐢 🐍 🦎 🐊 🐬 🐳 🦈 🐙 🦀 🐠 🐉 🦖 🐾 🌵 🌴 🌲 🍀 🌸 🌺 🌼 🌷 🍁 💎 👑 🪽" },
	{ n: "Comida", i: "🍕", e: "🍎 🍓 🍒 🍑 🍍 🥑 🥕 🌽 🍞 🥐 🥖 🧀 🍔 🍟 🌭 🌮 🌯 🥗 🍝 🍜 🍣 🍩 🍪 🎂 🍰 🧁 🍫 🍬 🍭 🍦 🥤 🧃 ☕ 🍵 🍷 🍹" },
	{ n: "Cosas", i: "🎨", e: "🎨 🖌️ 🖍️ ✂️ 🪮 💈 💅 💄 💋 👠 👗 👔 🧵 🧶 🪡 🧴 🧼 🪒 🛁 🔨 🔧 🪛 🧰 ⚙️ 🔌 💡 🔋 📷 🎥 🎬 🎙️ 🎧 🎮 🎲 🧩 ⚽ 🏀 🏈 🎾 🏐 🥊 🚲 🏍️ 🚀 🛸 🌎 🗺️ 🏠 🏢 🏛️ ⛪ 🏰 🗽" },
];
const CATEGORIAS = [{ n: "Escuela", i: "🏫", e: ICONOS_CANAL.join(" ") }, ...EXTRA, ...GRUPOS];

/** Donde vive la imagen de un ícono ("img:<id>" webp o "jpg:<id>"); null si el ícono es un emoji. */
export const iconoCanalUrl = (canalId: number, icono: string | null | undefined): string | null => {
	const m = /^(img|jpg):([0-9a-f]{8})$/.exec(icono || "");
	return m ? "https://wa-archivos.nyc3.digitaloceanspaces.com/equipo/iconos/c" + canalId + "-" + m[2] + (m[1] === "img" ? ".webp" : ".jpg") : null;
};

/** El ícono de un canal o grupo: su imagen o su emoji. */
export const IconoCanal: React.FC<{ id: number; icono: string }> = ({ id, icono }) => {
	const url = iconoCanalUrl(id, icono);
	return url ? <img className="eq-icono-img" src={url} alt="" loading="lazy" /> : <span className="eq-emoji">{icono}</span>;
};

/** Recorta al centro en cuadrado y achica a 256 px (WebP; JPEG si el navegador no sabe), como Cliq. */
const prepararImagen = (file: File): Promise<Blob> => new Promise((resolve, reject) => {
	const url = URL.createObjectURL(file);
	const img = new Image();
	img.onload = () => {
		const lado = Math.min(img.width, img.height);
		const c = document.createElement("canvas");
		c.width = 256; c.height = 256;
		const ctx = c.getContext("2d");
		if (!ctx) { URL.revokeObjectURL(url); reject(new Error("sin canvas")); return; }
		ctx.fillStyle = "#fff";
		ctx.fillRect(0, 0, 256, 256);
		ctx.drawImage(img, (img.width - lado) / 2, (img.height - lado) / 2, lado, lado, 0, 0, 256, 256);
		URL.revokeObjectURL(url);
		c.toBlob((b) => {
			if (b && b.type === "image/webp") resolve(b);
			else c.toBlob((j) => (j ? resolve(j) : reject(new Error("no se pudo"))), "image/jpeg", 0.85);
		}, "image/webp", 0.85);
	};
	img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Ese archivo no es una imagen.")); };
	img.src = url;
});

/**
 * Diálogo para elegir el ícono de un canal o grupo: un emoji (por categorías) o una imagen propia. onElegir(null) = volver
 * al de siempre. onSubir sube la imagen ya recortada.
 */
const IconoCanalPicker: React.FC<{ actual: string | null; canalId?: number; onElegir: (emoji: string | null) => void; onSubir?: (b: Blob) => Promise<void>; onCerrar: () => void }> = ({ actual, canalId, onElegir, onSubir, onCerrar }) => {
	const [cat, setCat] = useState<number>(0);
	const [subiendo, setSubiendo] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const archivo = useRef<HTMLInputElement>(null);
	useEffect(() => {
		const tecla = (e: KeyboardEvent) => { if (e.key === "Escape") onCerrar(); };
		window.addEventListener("keydown", tecla);
		return () => window.removeEventListener("keydown", tecla);
	}, [onCerrar]);
	const subir = async (f: File | undefined) => {
		if (!f || !onSubir) return;
		setSubiendo(true); setError(null);
		try { await onSubir(await prepararImagen(f)); }
		catch (e: any) { setError(e?.response?.data?.message || e?.message || "No se pudo subir la imagen."); }
		setSubiendo(false);
	};
	const lista = CATEGORIAS[cat].e.split(" ").filter(Boolean);
	const urlActual = canalId ? iconoCanalUrl(canalId, actual) : null;
	return (
		<div className="eqi-velo" onClick={onCerrar}>
			<div className="eqi-dlg" role="dialog" aria-label="Cambiar Ícono" onClick={(e) => e.stopPropagation()}>
				<div className="eqi-cab"><b>Cambiar Ícono</b>
					<button type="button" className="eqi-x" onClick={onCerrar} aria-label="Cerrar"><WappIcono n="x" /></button></div>
				{onSubir ? (
					<div className="eqi-subir">
						{urlActual ? <img className="eqi-actual" src={urlActual} alt="Ícono actual" /> : null}
						<button type="button" className="btn-wa" disabled={subiendo} onClick={() => archivo.current?.click()}>
							<WappIcono n="upload" />{subiendo ? "Subiendo…" : "Subir Imagen"}</button>
						<span className="eqi-nota">Se recorta cuadrada</span>
						<input ref={archivo} type="file" accept="image/*" hidden onChange={(e) => { subir(e.target.files?.[0]); e.target.value = ""; }} />
					</div>
				) : null}
				{error ? <div className="eqi-error" role="alert">{error}</div> : null}
				<div className="eqi-tabs" role="tablist">
					{CATEGORIAS.map((c, i) => <button key={c.n} type="button" role="tab" aria-selected={cat === i} className={cat === i ? "on" : ""} onClick={() => setCat(i)} title={c.n} aria-label={c.n}>{c.i}</button>)}
				</div>
				<div className="eqi-rejilla">
					{lista.map((e) => (
						<button type="button" key={e} className={"eqi-op" + (e === actual ? " on" : "")} onClick={() => onElegir(e)} aria-label={"Usar " + e} aria-pressed={e === actual}>{e}</button>
					))}
				</div>
				<div className="eqi-pie">
					<button type="button" className="btn-wa" disabled={!actual} onClick={() => onElegir(null)}>Quitar Ícono</button>
				</div>
			</div>
		</div>
	);
};

export default IconoCanalPicker;
